import { useCallback, useRef, useState } from 'react';

interface UseDialogWithDirtyCheckOptions {
    isDirty: boolean;
    onOpenChange: (open: boolean) => void;
    onDiscard?: () => void;
}

interface UseDialogWithDirtyCheckReturn {
    showDiscardModal: boolean;
    handleCancelDialog: (open: boolean) => void;
    handleDiscardChanges: () => void;
    handleCloseRequest: (e: React.MouseEvent) => boolean;
    handleDiscardModalChange: (show: boolean) => void;
    handleCancelButtonClick: () => void;
}

export const useDialogWithDirtyCheck = ({
    isDirty,
    onOpenChange,
    onDiscard,
}: UseDialogWithDirtyCheckOptions): UseDialogWithDirtyCheckReturn => {
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const pendingCloseRef = useRef(false);

    const handleCancelDialog = useCallback((open: boolean) => {
        if (!open && isDirty && !showDiscardModal) {
            setShowDiscardModal(true);
            pendingCloseRef.current = true;
            return;
        }

        if (open === false) {
            pendingCloseRef.current = false;
            if (onDiscard) {
                onDiscard();
            }
        }
        onOpenChange(open);
    }, [isDirty, showDiscardModal, onOpenChange, onDiscard]);

    const handleDiscardChanges = useCallback(() => {
        setShowDiscardModal(false);
        pendingCloseRef.current = false;
        if (onDiscard) {
            onDiscard();
        }
        onOpenChange(false);
    }, [onOpenChange, onDiscard]);

    const handleCloseRequest = useCallback((e: React.MouseEvent) => {
        if (isDirty && !showDiscardModal) {
            e.preventDefault();
            setShowDiscardModal(true);
            pendingCloseRef.current = true;
            return false;
        }
        return true;
    }, [isDirty, showDiscardModal]);

    const handleDiscardModalChange = useCallback((show: boolean) => {
        setShowDiscardModal(show);
        if (!show) {
            pendingCloseRef.current = false;
        }
    }, []);

    const handleCancelButtonClick = useCallback(() => {
        handleCancelDialog(false);
    }, [handleCancelDialog]);

    return {
        showDiscardModal,
        handleCancelDialog,
        handleDiscardChanges,
        handleCloseRequest,
        handleDiscardModalChange,
        handleCancelButtonClick,
    };
};

